import React, { useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  LinearProgress,
  Avatar,
  Badge,
  Tooltip,
  Divider,
  InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { styled, keyframes, alpha } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Diamond as DiamondIcon,
  ShoppingBag as ShoppingBagIcon,
  Person as PersonIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Payment as PaymentIcon,
  LocalShipping as ShippingIcon,
  TrackChanges as TrackIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Visibility as VisibilityIcon,
  Update as UpdateIcon,
  Cancel as CancelIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Pending as PendingIcon,
  DoneAll as DoneAllIcon
} from '@mui/icons-material';
import {
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { demoProducts, demoOrders, demoCustomers, demoMetalPrices } from '../data/demoData';
import { PriceCalculator } from '../utils/priceCalculator';
import './Admin.css';

// Animations
const glowPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(198, 164, 63, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(198, 164, 63, 0); }
  100% { box-shadow: 0 0 0 0 rgba(198, 164, 63, 0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

// Styled Components
const GlassCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(198, 164, 63, 0.2)',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 48px rgba(198, 164, 63, 0.2)',
    borderColor: 'rgba(198, 164, 63, 0.5)',
  },
}));

const StatCard = styled(GlassCard)(({ theme }) => ({
  padding: theme.spacing(2),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #C6A43F, #E5D5AC, #C6A43F)',
    animation: `${shimmer} 3s infinite`,
  },
}));

const AnimatedNumber = styled(motion.span)({
  fontSize: '2.5rem',
  fontWeight: 700,
  color: '#0A0F1E',
  fontFamily: 'Cormorant Garamond',
});

const StatusChip = styled(Chip)(({ status }) => ({
  backgroundColor: 
    status === 'delivered' ? '#4CAF50' :
    status === 'shipped' ? '#2196F3' :
    status === 'processing' ? '#FF9800' :
    status === 'confirmed' ? '#9C27B0' :
    status === 'cancelled' ? '#f44336' :
    status === 'refunded' ? '#FF5722' :
    '#9E9E9E',
  color: 'white',
  fontWeight: 600,
  '& .MuiChip-label': {
    letterSpacing: '1px',
  },
}));

const CourierChip = styled(Chip)(({ courier }) => ({
  backgroundColor: 
    courier === 'delhivery' ? '#FF6B6B' :
    courier === 'bluedart' ? '#4A90E2' :
    courier === 'fedex' ? '#4A154B' :
    courier === 'dhl' ? '#FFCC00' :
    courier === 'indiapost' ? '#0072CE' :
    '#9E9E9E',
  color: 'white',
  fontWeight: 600,
}));

const Admin = () => {
  const [tabValue, setTabValue] = useState(0);
  const [products, setProducts] = useState(demoProducts);
  const [orders, setOrders] = useState(demoOrders);
  // const [customers, setCustomers] = useState(demoCustomers);
  const customers = demoCustomers; // Not using state for customers as we are not modifying it in this component
  const [metalPrices, setMetalPrices] = useState(demoMetalPrices);
  const [openDialog, setOpenDialog] = useState(false);
  const [openOrderDialog, setOpenOrderDialog] = useState(false);
  const [openTrackingDialog, setOpenTrackingDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [calculator] = useState(new PriceCalculator(metalPrices));
  const [selectedTimeRange, setSelectedTimeRange] = useState('week');
  const [hoveredStat, setHoveredStat] = useState(null);
  // const [notificationCount, setNotificationCount] = useState(3);
  const notificationCount = 3; // Static count for demo purposes
  const [orderFilter, setOrderFilter] = useState('all');
  // const [expandedOrder, setExpandedOrder] = useState(null);

  // Order tracking state
  const [trackingInfo, setTrackingInfo] = useState({
    courierPartner: '',
    trackingNumber: '',
    estimatedDelivery: '',
    shippedDate: '',
    notes: ''
  });

  // Order status options
  const orderStatuses = [
    { value: 'pending', label: 'Pending', color: '#9E9E9E', icon: <PendingIcon /> },
    { value: 'confirmed', label: 'Confirmed', color: '#9C27B0', icon: <CheckCircleOutlineIcon /> },
    { value: 'processing', label: 'Processing', color: '#FF9800', icon: <UpdateIcon /> },
    { value: 'shipped', label: 'Shipped', color: '#2196F3', icon: <ShippingIcon /> },
    { value: 'delivered', label: 'Delivered', color: '#4CAF50', icon: <DoneAllIcon /> },
    { value: 'cancelled', label: 'Cancelled', color: '#f44336', icon: <CancelIcon /> },
    { value: 'refunded', label: 'Refunded', color: '#FF5722', icon: <PaymentIcon /> }
  ];

  // Courier partners
  const courierPartners = [
    { value: 'delhivery', label: 'Delhivery', logo: '🚚' },
    { value: 'bluedart', label: 'Blue Dart', logo: '✈️' },
    { value: 'fedex', label: 'FedEx', logo: '📦' },
    { value: 'dhl', label: 'DHL', logo: '🌐' },
    { value: 'indiapost', label: 'India Post', logo: '🏣' },
    { value: 'other', label: 'Other', logo: '📮' }
  ];

  // Demo data for charts
  const salesData = [
    { name: 'Mon', sales: 45000, orders: 12 },
    { name: 'Tue', sales: 52000, orders: 15 },
    { name: 'Wed', sales: 48000, orders: 13 },
    { name: 'Thu', sales: 61000, orders: 18 },
    { name: 'Fri', sales: 75000, orders: 22 },
    { name: 'Sat', sales: 82000, orders: 25 },
    { name: 'Sun', sales: 68000, orders: 20 },
  ];

  const categoryData = [
    { name: 'Gold', value: 45 },
    { name: 'Diamond', value: 30 },
    { name: 'Silver', value: 20 },
    { name: 'Platinum', value: 5 },
  ];

  const COLORS = ['#C6A43F', '#B3923A', '#E5D5AC', '#A5A5A5'];

  // Dashboard Stats
  const stats = {
    totalProducts: products.length,
    totalOrders: orders.length,
    totalCustomers: customers.length,
    totalRevenue: orders.reduce((sum, order) => sum + order.totalAmount, 0),
    lowStock: products.filter(p => p.stock < 5).length,
    pendingOrders: orders.filter(o => o.status === 'pending' || o.status === 'processing').length,
    shippedOrders: orders.filter(o => o.status === 'shipped').length,
    deliveredOrders: orders.filter(o => o.status === 'delivered').length,
    avgOrderValue: Math.round(orders.reduce((sum, order) => sum + order.totalAmount, 0) / orders.length) || 0,
    conversionRate: 68.5
  };

  // Product columns for DataGrid
  const productColumns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 70,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
          #{params.value}
        </Typography>
      )
    },
    { 
      field: 'name', 
      headerName: 'Product Name', 
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar 
            src={params.row.images?.[0]} 
            sx={{ 
              width: 32, 
              height: 32, 
              bgcolor: 'rgba(198, 164, 63, 0.1)',
              border: '1px solid rgba(198, 164, 63, 0.3)'
            }}
          >
            <DiamondIcon sx={{ fontSize: 16, color: '#C6A43F' }} />
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    { 
      field: 'category', 
      headerName: 'Category', 
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value}
          size="small"
          sx={{
            bgcolor: alpha('#C6A43F', 0.1),
            color: '#C6A43F',
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '0.7rem'
          }}
        />
      )
    },
    { 
      field: 'metalPurity', 
      headerName: 'Purity', 
      width: 80,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0A0F1E' }}>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'finalPrice', 
      headerName: 'Price (₹)', 
      width: 120,
      renderCell: (params) => (
        <Typography variant="body1" sx={{ fontWeight: 700, color: '#C6A43F' }}>
          ₹{params.value?.toLocaleString()}
        </Typography>
      )
    },
    { 
      field: 'stock', 
      headerName: 'Stock', 
      width: 80,
      renderCell: (params) => (
        <Badge
          color={params.value < 5 ? 'error' : params.value < 10 ? 'warning' : 'success'}
          variant="dot"
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, ml: 1 }}>
            {params.value}
          </Typography>
        </Badge>
      )
    },
    { 
      field: 'type', 
      headerName: 'Pricing', 
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value}
          size="small"
          sx={{
            bgcolor: params.value === 'fixed' ? alpha('#4CAF50', 0.1) : alpha('#C6A43F', 0.1),
            color: params.value === 'fixed' ? '#4CAF50' : '#C6A43F',
            fontWeight: 600,
            textTransform: 'capitalize'
          }}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
            <IconButton 
              size="small" 
              onClick={() => handleEditProduct(params.row)}
              sx={{
                bgcolor: alpha('#C6A43F', 0.1),
                '&:hover': { bgcolor: alpha('#C6A43F', 0.2) }
              }}
            >
              <EditIcon sx={{ fontSize: 18, color: '#C6A43F' }} />
            </IconButton>
          </motion.div>
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
            <IconButton 
              size="small" 
              onClick={() => handleDeleteProduct(params.row.id)}
              sx={{
                bgcolor: alpha('#f44336', 0.1),
                '&:hover': { bgcolor: alpha('#f44336', 0.2) }
              }}
            >
              <DeleteIcon sx={{ fontSize: 18, color: '#f44336' }} />
            </IconButton>
          </motion.div>
        </Box>
      )
    }
  ];

  // Enhanced Order columns for DataGrid
  const orderColumns = [
    { 
      field: 'id', 
      headerName: 'Order ID', 
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#C6A43F' }}>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'customerName', 
      headerName: 'Customer', 
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 28, height: 28, bgcolor: alpha('#C6A43F', 0.2), color: '#C6A43F' }}>
            {params.value?.charAt(0) || 'U'}
          </Avatar>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      )
    },
    { 
      field: 'customerPhone', 
      headerName: 'Phone', 
      width: 120 
    },
    { 
      field: 'totalAmount', 
      headerName: 'Amount (₹)', 
      width: 120,
      renderCell: (params) => (
        <Typography variant="body1" sx={{ fontWeight: 700, color: '#0A0F1E' }}>
          ₹{params.value?.toLocaleString()}
        </Typography>
      )
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 130,
      renderCell: (params) => (
        <StatusChip 
          label={params.value?.toUpperCase()} 
          status={params.value}
          size="small"
        />
      )
    },
    { 
      field: 'paymentStatus', 
      headerName: 'Payment', 
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value}
          size="small"
          icon={<PaymentIcon sx={{ fontSize: 14 }} />}
          sx={{
            bgcolor: params.value === 'completed' ? alpha('#4CAF50', 0.1) : alpha('#FF9800', 0.1),
            color: params.value === 'completed' ? '#4CAF50' : '#FF9800',
            fontWeight: 600
          }}
        />
      )
    },
    { 
      field: 'courierPartner', 
      headerName: 'Courier', 
      width: 120,
      renderCell: (params) => (
        params.value ? (
          <CourierChip 
            label={params.value}
            courier={params.value}
            size="small"
          />
        ) : (
          <Typography variant="caption" color="text.secondary">Not assigned</Typography>
        )
      )
    },
    { 
      field: 'trackingNumber', 
      headerName: 'Tracking', 
      width: 120,
      renderCell: (params) => (
        params.value ? (
          <Chip 
            label={params.value}
            size="small"
            icon={<TrackIcon sx={{ fontSize: 14 }} />}
            sx={{ bgcolor: alpha('#2196F3', 0.1), color: '#2196F3' }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">-</Typography>
        )
      )
    },
    { 
      field: 'orderDate', 
      headerName: 'Order Date', 
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="View Details">
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <IconButton 
                size="small" 
                onClick={() => handleViewOrder(params.row)}
                sx={{
                  bgcolor: alpha('#C6A43F', 0.1),
                  '&:hover': { bgcolor: alpha('#C6A43F', 0.2) }
                }}
              >
                <VisibilityIcon sx={{ fontSize: 18, color: '#C6A43F' }} />
              </IconButton>
            </motion.div>
          </Tooltip>
          
          <Tooltip title="Update Status">
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <IconButton 
                size="small" 
                onClick={() => handleUpdateOrder(params.row)}
                sx={{
                  bgcolor: alpha('#2196F3', 0.1),
                  '&:hover': { bgcolor: alpha('#2196F3', 0.2) }
                }}
              >
                <UpdateIcon sx={{ fontSize: 18, color: '#2196F3' }} />
              </IconButton>
            </motion.div>
          </Tooltip>
          
          <Tooltip title="Add Tracking">
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <IconButton 
                size="small" 
                onClick={() => handleAddTracking(params.row)}
                sx={{
                  bgcolor: alpha('#4CAF50', 0.1),
                  '&:hover': { bgcolor: alpha('#4CAF50', 0.2) }
                }}
              >
                <ShippingIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
              </IconButton>
            </motion.div>
          </Tooltip>
          
          <Tooltip title="Print Invoice">
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <IconButton 
                size="small" 
                onClick={() => handlePrintInvoice(params.row)}
                sx={{
                  bgcolor: alpha('#FF9800', 0.1),
                  '&:hover': { bgcolor: alpha('#FF9800', 0.2) }
                }}
              >
                <PrintIcon sx={{ fontSize: 18, color: '#FF9800' }} />
              </IconButton>
            </motion.div>
          </Tooltip>
        </Box>
      )
    }
  ];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setNewProduct(product);
    setOpenDialog(true);
  };

  const handleDeleteProduct = (productId) => {
    setProducts(products.filter(p => p.id !== productId));
    setSnackbar({
      open: true,
      message: '✅ Product deleted successfully',
      severity: 'success'
    });
  };

  const [newProduct, setNewProduct] = useState({
    name: '',
    category: 'gold',
    metalType: 'gold',
    metalPurity: '22k',
    type: 'fixed',
    metalWeight: 0,
    labourCharge: 0,
    wastagePercentage: 0,
    basePrice: 0,
    finalPrice: 0,
    stock: 0,
    description: ''
  });

  const handleAddProduct = () => {
    setEditingProduct(null);
    setNewProduct({
      name: '',
      category: 'gold',
      metalType: 'gold',
      metalPurity: '22k',
      type: 'fixed',
      metalWeight: 0,
      labourCharge: 0,
      wastagePercentage: 0,
      basePrice: 0,
      finalPrice: 0,
      stock: 0,
      description: ''
    });
    setOpenDialog(true);
  };

  const handleSaveProduct = () => {
    if (editingProduct) {
      setProducts(products.map(p => 
        p.id === editingProduct.id ? { ...newProduct, id: p.id } : p
      ));
      setSnackbar({
        open: true,
        message: '✨ Product updated successfully',
        severity: 'success'
      });
    } else {
      const finalPrice = newProduct.type === 'fixed' 
        ? newProduct.basePrice 
        : calculator.calculatePrice(newProduct);
      
      setProducts([...products, {
        ...newProduct,
        id: products.length + 1,
        finalPrice,
        rating: 0,
        reviews: 0,
        isNew: true,
        createdAt: new Date().toISOString().split('T')[0]
      }]);
      setSnackbar({
        open: true,
        message: '🎉 New product added successfully',
        severity: 'success'
      });
    }
    setOpenDialog(false);
  };

  const handleUpdateMetalPrices = () => {
    setLoading(true);
    
    setTimeout(() => {
      calculator.updateMetalPrices(metalPrices.gold, metalPrices.silver);
      
      const updatedProducts = products.map(product => {
        if (product.type !== 'fixed') {
          return {
            ...product,
            finalPrice: calculator.calculatePrice(product)
          };
        }
        return product;
      });
      
      setProducts(updatedProducts);
      setLoading(false);
      
      setSnackbar({
        open: true,
        message: '💎 Metal prices updated and products recalculated',
        severity: 'success'
      });
    }, 2000);
  };

  // Order Management Functions
  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setOpenOrderDialog(true);
  };

  const handleUpdateOrder = (order) => {
    setSelectedOrder(order);
    setOpenTrackingDialog(true);
  };

  const handleAddTracking = (order) => {
    setSelectedOrder(order);
    setTrackingInfo({
      courierPartner: order.courierPartner || '',
      trackingNumber: order.trackingNumber || '',
      estimatedDelivery: order.estimatedDelivery || '',
      shippedDate: order.shippedDate || '',
      notes: order.trackingNotes || ''
    });
    setOpenTrackingDialog(true);
  };

  const handleUpdateOrderStatus = (orderId, newStatus) => {
    setOrders(orders.map(order => 
      order.id === orderId ? { ...order, status: newStatus } : order
    ));
    setSnackbar({
      open: true,
      message: `📦 Order status updated to ${newStatus}`,
      severity: 'success'
    });
  };

  const handleSaveTracking = () => {
    setOrders(orders.map(order => 
      order.id === selectedOrder.id ? { 
        ...order, 
        ...trackingInfo,
        status: 'shipped'
      } : order
    ));
    setOpenTrackingDialog(false);
    setSnackbar({
      open: true,
      message: '🚚 Tracking information saved successfully',
      severity: 'success'
    });
  };

  const handlePrintInvoice = (order) => {
    // Simulate printing invoice
    setSnackbar({
      open: true,
      message: '🖨️ Invoice generated and ready to print',
      severity: 'info'
    });
  };

  const handleNotifyCustomer = (order, method) => {
    setSnackbar({
      open: true,
      message: `📨 Notification sent via ${method}`,
      severity: 'success'
    });
  };

  const getFilteredOrders = () => {
    if (orderFilter === 'all') return orders;
    return orders.filter(order => order.status === orderFilter);
  };

  return (
    <Box className="admin-dashboard">
      {/* Animated Background */}
      <Box className="admin-bg">
        <div className="admin-bg-gradient"></div>
        <div className="admin-bg-particles">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="admin-particle"
              animate={{
                y: [0, -100, 0],
                x: [0, i % 2 === 0 ? 50 : -50, 0],
                rotate: [0, 180, 360],
              }}
              transition={{
                duration: 15 + i % 10,
                repeat: Infinity,
                delay: i * 0.2,
              }}
            />
          ))}
        </div>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4, position: 'relative', zIndex: 2 }}>
        {/* Header with Animation */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, type: "spring" }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 4,
            pb: 2,
            borderBottom: '2px solid rgba(198, 164, 63, 0.2)'
          }}>
            <Box>
              <Typography variant="h2" className="admin-title">
                Admin Dashboard
              </Typography>
              <Typography variant="subtitle1" className="admin-subtitle">
                Manage your MIRAE jewelry empire
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Tooltip title="Notifications">
                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                  <Badge badgeContent={notificationCount} color="error" sx={{ '& .MuiBadge-badge': { bgcolor: '#C6A43F' } }}>
                    <IconButton sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}>
                      <AssessmentIcon sx={{ color: 'white' }} />
                    </IconButton>
                  </Badge>
                </motion.div>
              </Tooltip>
              
              <Tooltip title="Settings">
                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                  <IconButton sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}>
                    <SettingsIcon sx={{ color: 'white' }} />
                  </IconButton>
                </motion.div>
              </Tooltip>
            </Box>
          </Box>
        </motion.div>

        {/* Stats Cards with Animations */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            { 
              icon: <DiamondIcon />, 
              title: 'Total Products', 
              value: stats.totalProducts, 
              change: '+12%', 
              subtext: `${stats.lowStock} low in stock`,
              color: '#C6A43F'
            },
            { 
              icon: <ShoppingBagIcon />, 
              title: 'Total Orders', 
              value: stats.totalOrders, 
              change: '+8%', 
              subtext: `${stats.pendingOrders} pending`,
              color: '#2196F3'
            },
            { 
              icon: <ShippingIcon />, 
              title: 'Shipped Orders', 
              value: stats.shippedOrders, 
              change: '+5%', 
              subtext: 'In transit',
              color: '#FF9800'
            },
            { 
              icon: <DoneAllIcon />, 
              title: 'Delivered', 
              value: stats.deliveredOrders, 
              change: '+15%', 
              subtext: 'Completed',
              color: '#4CAF50'
            },
            { 
              icon: <PersonIcon />, 
              title: 'Total Customers', 
              value: stats.totalCustomers, 
              change: '+15%', 
              subtext: '↑ 23 this week',
              color: '#4CAF50'
            },
            { 
              icon: <MoneyIcon />, 
              title: 'Total Revenue', 
              value: `₹${stats.totalRevenue.toLocaleString()}`, 
              change: '+18%', 
              subtext: `Avg: ₹${stats.avgOrderValue.toLocaleString()}`,
              color: '#FF9800'
            }
          ].map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -5 }}
                onHoverStart={() => setHoveredStat(index)}
                onHoverEnd={() => setHoveredStat(null)}
              >
                <StatCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="overline" sx={{ letterSpacing: '2px', fontSize: '0.6rem' }}>
                          {stat.title}
                        </Typography>
                        <AnimatedNumber
                          animate={{ scale: hoveredStat === index ? [1, 1.2, 1] : 1 }}
                          transition={{ duration: 0.5 }}
                          sx={{ fontSize: '1.8rem' }}
                        >
                          {stat.value}
                        </AnimatedNumber>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Chip 
                            label={stat.change}
                            size="small"
                            sx={{
                              bgcolor: alpha(stat.color, 0.1),
                              color: stat.color,
                              fontWeight: 600,
                              fontSize: '0.6rem',
                              height: '20px'
                            }}
                          />
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.6rem' }}>
                            {stat.subtext}
                          </Typography>
                        </Box>
                      </Box>
                      <motion.div
                        animate={{ 
                          rotate: hoveredStat === index ? [0, -10, 10, -10, 10, 0] : 0,
                          scale: hoveredStat === index ? [1, 1.2, 1] : 1
                        }}
                        transition={{ duration: 0.5 }}
                      >
                        <Avatar sx={{ 
                          bgcolor: alpha(stat.color, 0.1),
                          color: stat.color,
                          width: 40,
                          height: 40,
                          animation: `${float} 3s ease-in-out infinite`
                        }}>
                          {stat.icon}
                        </Avatar>
                      </motion.div>
                    </Box>
                  </CardContent>
                </StatCard>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={8}>
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <GlassCard>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TimelineIcon sx={{ color: '#C6A43F' }} />
                      <Typography variant="h6" sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}>
                        Sales Analytics
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {['week', 'month', 'year'].map((range) => (
                        <motion.div key={range} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                          <Button
                            size="small"
                            variant={selectedTimeRange === range ? 'contained' : 'outlined'}
                            onClick={() => setSelectedTimeRange(range)}
                            sx={{
                              bgcolor: selectedTimeRange === range ? '#C6A43F' : 'transparent',
                              color: selectedTimeRange === range ? '#0A0F1E' : '#C6A43F',
                              borderColor: '#C6A43F',
                              '&:hover': {
                                bgcolor: selectedTimeRange === range ? '#B3923A' : 'rgba(198, 164, 63, 0.04)',
                              }
                            }}
                          >
                            {range.charAt(0).toUpperCase() + range.slice(1)}
                          </Button>
                        </motion.div>
                      ))}
                    </Box>
                  </Box>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={salesData}>
                      <defs>
                        <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#C6A43F" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#C6A43F" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(198,164,63,0.1)" />
                      <XAxis dataKey="name" stroke="#666" />
                      <YAxis stroke="#666" />
                      <RechartsTooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(10,15,30,0.9)', 
                          border: '1px solid #C6A43F',
                          borderRadius: '8px',
                          color: 'white'
                        }} 
                      />
                      <Legend />
                      <Area type="monotone" dataKey="sales" stroke="#C6A43F" fillOpacity={1} fill="url(#colorSales)" name="Sales (₹)" />
                      <Line type="monotone" dataKey="orders" stroke="#E5D5AC" strokeWidth={2} name="Orders" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </GlassCard>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <GlassCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <PieChart sx={{ color: '#C6A43F' }} />
                    <Typography variant="h6" sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}>
                      Category Distribution
                    </Typography>
                  </Box>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                    {categoryData.map((category, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: COLORS[index], borderRadius: '2px' }} />
                        <Typography variant="caption">{category.name}</Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </GlassCard>
            </motion.div>
          </Grid>
        </Grid>

        {/* Tabs with Animation */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <Paper sx={{ 
            mb: 3, 
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid rgba(198, 164, 63, 0.2)',
            background: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  py: 2,
                  transition: 'all 0.3s ease',
                  '&.Mui-selected': {
                    color: '#C6A43F',
                    backgroundColor: 'rgba(198, 164, 63, 0.05)',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(198, 164, 63, 0.02)',
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#C6A43F',
                  height: 3,
                }
              }}
            >
              <Tab label="Products" icon={<DiamondIcon />} iconPosition="start" />
              <Tab label="Orders" icon={<ShoppingBagIcon />} iconPosition="start" />
              <Tab label="Customers" icon={<PersonIcon />} iconPosition="start" />
              <Tab label="Metal Prices" icon={<TrendingUpIcon />} iconPosition="start" />
            </Tabs>
          </Paper>
        </motion.div>

        {/* Tab Panels with Animations */}
        <AnimatePresence mode="wait">
          <motion.div
            key={tabValue}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            <Paper sx={{ 
              p: 3, 
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(198, 164, 63, 0.2)',
              minHeight: '600px'
            }}>
              {tabValue === 0 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 700, color: '#0A0F1E' }}>
                      Product Management
                    </Typography>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddProduct}
                        sx={{
                          bgcolor: '#C6A43F',
                          color: '#0A0F1E',
                          fontWeight: 600,
                          px: 3,
                          '&:hover': { 
                            bgcolor: '#B3923A',
                            boxShadow: '0 8px 20px rgba(198, 164, 63, 0.3)'
                          },
                        }}
                      >
                        Add New Product
                      </Button>
                    </motion.div>
                  </Box>
                  <Box sx={{ height: 500, width: '100%' }}>
                    <DataGrid
                      rows={products}
                      columns={productColumns}
                      pageSize={10}
                      rowsPerPageOptions={[10]}
                      disableSelectionOnClick
                      sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                          bgcolor: '#0A0F1E',
                          color: 'white',
                          fontSize: '0.9rem',
                          fontWeight: 600,
                        },
                        '& .MuiDataGrid-cell': {
                          borderBottom: '1px solid rgba(198, 164, 63, 0.1)',
                        },
                        '& .MuiDataGrid-row:hover': {
                          bgcolor: 'rgba(198, 164, 63, 0.02)',
                        }
                      }}
                    />
                  </Box>
                </Box>
              )}

              {tabValue === 1 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 700, color: '#0A0F1E' }}>
                      Order Management
                    </Typography>
                    
                    {/* Order Filter */}
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <Select
                        value={orderFilter}
                        onChange={(e) => setOrderFilter(e.target.value)}
                        displayEmpty
                        sx={{
                          bgcolor: 'white',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(198, 164, 63, 0.3)',
                          }
                        }}
                      >
                        <MenuItem value="all">All Orders</MenuItem>
                        {orderStatuses.map(status => (
                          <MenuItem key={status.value} value={status.value}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ 
                                width: 10, 
                                height: 10, 
                                borderRadius: '50%', 
                                bgcolor: status.color 
                              }} />
                              {status.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  
                  {/* Order Stats Summary */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {orderStatuses.map(status => {
                      const count = orders.filter(o => o.status === status.value).length;
                      return (
                        <Grid item xs={6} sm={3} md={1.7} key={status.value}>
                          <Paper sx={{ 
                            p: 1.5, 
                            textAlign: 'center',
                            bgcolor: alpha(status.color, 0.05),
                            border: '1px solid',
                            borderColor: alpha(status.color, 0.2),
                            borderRadius: '8px'
                          }}>
                            <Typography variant="caption" sx={{ color: status.color, fontWeight: 600 }}>
                              {status.label}
                            </Typography>
                            <Typography variant="h6" sx={{ color: status.color, fontWeight: 700 }}>
                              {count}
                            </Typography>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                  
                  <Box sx={{ height: 500, width: '100%' }}>
                    <DataGrid
                      rows={getFilteredOrders()}
                      columns={orderColumns}
                      pageSize={10}
                      rowsPerPageOptions={[10]}
                      disableSelectionOnClick
                      sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                          bgcolor: '#0A0F1E',
                          color: 'white',
                        },
                        '& .MuiDataGrid-cell': {
                          borderBottom: '1px solid rgba(198, 164, 63, 0.1)',
                        }
                      }}
                    />
                  </Box>
                </Box>
              )}

              {tabValue === 2 && (
                <Box>
                  <Typography variant="h5" sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 700, color: '#0A0F1E', mb: 3 }}>
                    Customer Management
                  </Typography>
                  <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid rgba(198,164,63,0.1)' }}>
                    <Table>
                      <TableHead sx={{ bgcolor: '#0A0F1E' }}>
                        <TableRow>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Customer ID</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Name</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Email</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Phone</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Total Orders</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Total Spent</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customers.map((customer) => (
                          <TableRow key={customer.id} sx={{ '&:hover': { bgcolor: 'rgba(198,164,63,0.02)' } }}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                {customer.id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: alpha('#C6A43F', 0.2), color: '#C6A43F' }}>
                                  {customer.name?.charAt(0)}
                                </Avatar>
                                {customer.name}
                              </Box>
                            </TableCell>
                            <TableCell>{customer.email}</TableCell>
                            <TableCell>{customer.phone}</TableCell>
                            <TableCell>
                              <Chip 
                                label={customer.totalOrders}
                                size="small"
                                sx={{ bgcolor: alpha('#C6A43F', 0.1), color: '#C6A43F', fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1" sx={{ fontWeight: 700, color: '#C6A43F' }}>
                                ₹{customer.totalSpent?.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" sx={{ color: '#C6A43F' }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {tabValue === 3 && (
                <Box>
                  <Typography variant="h5" sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 700, color: '#0A0F1E', mb: 4 }}>
                    Metal Price Management
                  </Typography>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <motion.div
                        whileHover={{ y: -5 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Card className="price-card gold-card">
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                              <motion.div
                                animate={{ rotate: [0, 360] }}
                                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                              >
                                <Avatar sx={{ bgcolor: alpha('#C6A43F', 0.2), width: 56, height: 56 }}>
                                  <span style={{ fontSize: '2rem' }}>✨</span>
                                </Avatar>
                              </motion.div>
                              <Box sx={{ ml: 2 }}>
                                <Typography variant="h5" sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 700, color: '#C6A43F' }}>
                                  Gold (22K)
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  Hallmarked · 100% Pure
                                </Typography>
                              </Box>
                            </Box>
                            <TextField
                              fullWidth
                              label="Price per Gram (₹)"
                              type="number"
                              value={metalPrices.gold}
                              onChange={(e) => setMetalPrices({ ...metalPrices, gold: parseInt(e.target.value) })}
                              InputProps={{
                                startAdornment: <Typography sx={{ mr: 1, color: '#C6A43F', fontWeight: 600 }}>₹</Typography>,
                              }}
                              sx={{ mb: 2 }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="textSecondary">
                                Last updated: {new Date(metalPrices.lastUpdated).toLocaleString()}
                              </Typography>
                              <Chip 
                                label="Live" 
                                size="small"
                                sx={{ 
                                  bgcolor: alpha('#4CAF50', 0.1), 
                                  color: '#4CAF50',
                                  animation: `${glowPulse} 2s infinite`
                                }} 
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <motion.div
                        whileHover={{ y: -5 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Card className="price-card silver-card">
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                              <motion.div
                                animate={{ rotate: [0, -360] }}
                                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                              >
                                <Avatar sx={{ bgcolor: alpha('#A5A5A5', 0.2), width: 56, height: 56 }}>
                                  <span style={{ fontSize: '2rem' }}>⚡</span>
                                </Avatar>
                              </motion.div>
                              <Box sx={{ ml: 2 }}>
                                <Typography variant="h5" sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 700, color: '#A5A5A5' }}>
                                  Silver (925)
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  Sterling Silver · Hallmarked
                                </Typography>
                              </Box>
                            </Box>
                            <TextField
                              fullWidth
                              label="Price per Gram (₹)"
                              type="number"
                              value={metalPrices.silver}
                              onChange={(e) => setMetalPrices({ ...metalPrices, silver: parseInt(e.target.value) })}
                              InputProps={{
                                startAdornment: <Typography sx={{ mr: 1, color: '#A5A5A5', fontWeight: 600 }}>₹</Typography>,
                              }}
                              sx={{ mb: 2 }}
                            />
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  </Grid>
                  
                  {loading && (
                    <Box sx={{ mt: 3 }}>
                      <LinearProgress 
                        sx={{ 
                          height: 6, 
                          borderRadius: 3,
                          bgcolor: 'rgba(198, 164, 63, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#C6A43F',
                          }
                        }} 
                      />
                      <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: '#C6A43F' }}>
                        Updating product prices...
                      </Typography>
                    </Box>
                  )}
                  
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={handleUpdateMetalPrices}
                      disabled={loading}
                      sx={{
                        mt: 4,
                        bgcolor: '#0A0F1E',
                        color: 'white',
                        py: 2,
                        fontSize: '1rem',
                        fontWeight: 600,
                        letterSpacing: '2px',
                        '&:hover': { 
                          bgcolor: '#C6A43F',
                          color: '#0A0F1E',
                          boxShadow: '0 10px 30px rgba(198, 164, 63, 0.4)'
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {loading ? 'UPDATING PRICES...' : 'UPDATE METAL PRICES & RECALCULATE ALL PRODUCTS'}
                    </Button>
                  </motion.div>
                </Box>
              )}
            </Paper>
          </motion.div>
        </AnimatePresence>
      </Container>

      {/* Product Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 30px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(198,164,63,0.2)',
          }
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <DialogTitle sx={{ 
            fontFamily: 'Cormorant Garamond', 
            fontWeight: 700, 
            fontSize: '1.8rem',
            color: '#0A0F1E',
            borderBottom: '1px solid rgba(198,164,63,0.2)',
            pb: 2
          }}>
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Product Name"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#C6A43F',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#C6A43F',
                        borderWidth: '2px',
                      },
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={newProduct.category}
                    label="Category"
                    onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                  >
                    <MenuItem value="gold">✨ Gold</MenuItem>
                    <MenuItem value="silver">⚡ Silver</MenuItem>
                    <MenuItem value="diamond">💎 Diamond</MenuItem>
                    <MenuItem value="platinum">⭐ Platinum</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Pricing Type</InputLabel>
                  <Select
                    value={newProduct.type}
                    label="Pricing Type"
                    onChange={(e) => setNewProduct({ ...newProduct, type: e.target.value })}
                  >
                    <MenuItem value="fixed">💎 Fixed Price</MenuItem>
                    <MenuItem value="wastage">⚖️ Wastage Calculation</MenuItem>
                    <MenuItem value="labour">🔨 Labour + Metal</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {newProduct.type !== 'fixed' && (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Metal Weight (g)"
                      type="number"
                      value={newProduct.metalWeight}
                      onChange={(e) => setNewProduct({ ...newProduct, metalWeight: parseFloat(e.target.value) })}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <InputLabel>Metal Purity</InputLabel>
                      <Select
                        value={newProduct.metalPurity}
                        label="Metal Purity"
                        onChange={(e) => setNewProduct({ ...newProduct, metalPurity: e.target.value })}
                      >
                        <MenuItem value="24k">24K (99.9%)</MenuItem>
                        <MenuItem value="22k">22K (91.6%)</MenuItem>
                        <MenuItem value="18k">18K (75%)</MenuItem>
                        <MenuItem value="14k">14K (58.5%)</MenuItem>
                        <MenuItem value="925">925 Silver</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Labour Charge (₹)"
                      type="number"
                      value={newProduct.labourCharge}
                      onChange={(e) => setNewProduct({ ...newProduct, labourCharge: parseInt(e.target.value) })}
                    />
                  </Grid>
                  
                  {newProduct.type === 'wastage' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Wastage Percentage (%)"
                        type="number"
                        value={newProduct.wastagePercentage}
                        onChange={(e) => setNewProduct({ ...newProduct, wastagePercentage: parseFloat(e.target.value) })}
                      />
                    </Grid>
                  )}
                </>
              )}
              
              {newProduct.type === 'fixed' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Base Price (₹)"
                    type="number"
                    value={newProduct.basePrice}
                    onChange={(e) => setNewProduct({ ...newProduct, basePrice: parseInt(e.target.value) })}
                  />
                </Grid>
              )}
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Stock Quantity"
                  type="number"
                  value={newProduct.stock}
                  onChange={(e) => setNewProduct({ ...newProduct, stock: parseInt(e.target.value) })}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={4}
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(198,164,63,0.1)' }}>
            <Button 
              onClick={() => setOpenDialog(false)}
              sx={{ color: '#666' }}
            >
              Cancel
            </Button>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button 
                onClick={handleSaveProduct}
                variant="contained"
                sx={{
                  bgcolor: '#C6A43F',
                  color: '#0A0F1E',
                  fontWeight: 600,
                  px: 4,
                  '&:hover': { 
                    bgcolor: '#B3923A',
                    boxShadow: '0 8px 20px rgba(198, 164, 63, 0.3)'
                  },
                }}
              >
                {editingProduct ? 'Update Product' : 'Add Product'}
              </Button>
            </motion.div>
          </DialogActions>
        </motion.div>
      </Dialog>

      {/* Order View Dialog */}
      <Dialog
        open={openOrderDialog}
        onClose={() => setOpenOrderDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        {selectedOrder && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <DialogTitle sx={{ 
              fontFamily: 'Cormorant Garamond', 
              fontWeight: 700, 
              fontSize: '1.8rem',
              color: '#0A0F1E',
              borderBottom: '1px solid rgba(198,164,63,0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              Order Details - {selectedOrder.id}
              <Chip 
                label={selectedOrder.status}
                size="small"
                sx={{ bgcolor: alpha('#C6A43F', 0.1), color: '#C6A43F' }}
              />
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={3}>
                {/* Customer Info */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#C6A43F', mb: 2 }}>
                    Customer Information
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: alpha('#C6A43F', 0.05) }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: '#C6A43F' }}>
                        {selectedOrder.customerName?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="h6">{selectedOrder.customerName}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedOrder.customerPhone}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2">
                      Email: {selectedOrder.customerEmail || 'Not provided'}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Shipping Info */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#C6A43F', mb: 2 }}>
                    Shipping Address
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: alpha('#C6A43F', 0.05) }}>
                    <Typography variant="body2">
                      {selectedOrder.shippingDetails?.fullName || selectedOrder.customerName}<br />
                      {selectedOrder.shippingDetails?.addressLine1 || '123, Jewel Street'}<br />
                      {selectedOrder.shippingDetails?.city || 'Mumbai'}, {selectedOrder.shippingDetails?.state || 'Maharashtra'}<br />
                      {selectedOrder.shippingDetails?.pincode || '400001'}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Order Items */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#C6A43F', mb: 2 }}>
                    Order Items
                  </Typography>
                  <TableContainer component={Paper} sx={{ bgcolor: alpha('#C6A43F', 0.05) }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell align="right">Price</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedOrder.items?.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar src={item.images?.[0]} sx={{ width: 40, height: 40 }} />
                                <Box>
                                  <Typography variant="body2">{item.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {item.metalPurity}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right">₹{item.finalPrice?.toLocaleString()}</TableCell>
                            <TableCell align="right">₹{(item.finalPrice * item.quantity).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={3} align="right"><strong>Total</strong></TableCell>
                          <TableCell align="right"><strong>₹{selectedOrder.totalAmount?.toLocaleString()}</strong></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Tracking Info */}
                {selectedOrder.trackingNumber && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#C6A43F', mb: 2 }}>
                      Tracking Information
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: alpha('#C6A43F', 0.05) }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">
                            Courier Partner
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {selectedOrder.courierPartner}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">
                            Tracking Number
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {selectedOrder.trackingNumber}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">
                            Shipped Date
                          </Typography>
                          <Typography variant="body2">
                            {selectedOrder.shippedDate || 'Not available'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">
                            Estimated Delivery
                          </Typography>
                          <Typography variant="body2">
                            {selectedOrder.estimatedDelivery || 'Not available'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                )}

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      onClick={() => handlePrintInvoice(selectedOrder)}
                      sx={{ borderColor: '#C6A43F', color: '#C6A43F' }}
                    >
                      Print Invoice
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<ShippingIcon />}
                      onClick={() => {
                        setOpenOrderDialog(false);
                        handleAddTracking(selectedOrder);
                      }}
                      sx={{ bgcolor: '#0A0F1E', '&:hover': { bgcolor: '#C6A43F' } }}
                    >
                      Update Tracking
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
          </motion.div>
        )}
      </Dialog>

      {/* Tracking Dialog */}
      <Dialog
        open={openTrackingDialog}
        onClose={() => setOpenTrackingDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
          }
        }}
      >
        <DialogTitle sx={{ 
          fontFamily: 'Cormorant Garamond', 
          fontWeight: 700, 
          fontSize: '1.5rem',
          color: '#0A0F1E',
          borderBottom: '1px solid rgba(198,164,63,0.2)'
        }}>
          Update Tracking Information
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            {/* Order Status Update */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#C6A43F' }}>
                Order Status
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedOrder?.status || 'pending'}
                  onChange={(e) => handleUpdateOrderStatus(selectedOrder?.id, e.target.value)}
                >
                  {orderStatuses.map(status => (
                    <MenuItem key={status.value} value={status.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: status.color }} />
                        {status.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Courier Partner */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#C6A43F' }}>
                Courier Partner
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={trackingInfo.courierPartner}
                  onChange={(e) => setTrackingInfo({ ...trackingInfo, courierPartner: e.target.value })}
                >
                  {courierPartners.map(courier => (
                    <MenuItem key={courier.value} value={courier.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontSize: '1.2rem' }}>{courier.logo}</span>
                        {courier.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Tracking Number */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tracking Number"
                value={trackingInfo.trackingNumber}
                onChange={(e) => setTrackingInfo({ ...trackingInfo, trackingNumber: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TrackIcon sx={{ color: '#C6A43F' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Shipped Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Shipped Date"
                type="date"
                value={trackingInfo.shippedDate}
                onChange={(e) => setTrackingInfo({ ...trackingInfo, shippedDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Estimated Delivery */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Estimated Delivery"
                type="date"
                value={trackingInfo.estimatedDelivery}
                onChange={(e) => setTrackingInfo({ ...trackingInfo, estimatedDelivery: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                multiline
                rows={3}
                value={trackingInfo.notes}
                onChange={(e) => setTrackingInfo({ ...trackingInfo, notes: e.target.value })}
                placeholder="Any special instructions or notes for the customer..."
              />
            </Grid>

            {/* Notify Customer Options */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#C6A43F' }}>
                Notify Customer
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={() => handleNotifyCustomer(selectedOrder, 'email')}
                  sx={{ borderColor: '#C6A43F', color: '#C6A43F', flex: 1 }}
                >
                  Email
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<WhatsAppIcon />}
                  onClick={() => handleNotifyCustomer(selectedOrder, 'whatsapp')}
                  sx={{ borderColor: '#25D366', color: '#25D366', flex: 1 }}
                >
                  WhatsApp
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(198,164,63,0.1)' }}>
          <Button onClick={() => setOpenTrackingDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTracking}
            sx={{ bgcolor: '#C6A43F', color: '#0A0F1E', '&:hover': { bgcolor: '#B3923A' } }}
          >
            Save Tracking Info
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            icon={snackbar.severity === 'success' ? <CheckCircleIcon /> : null}
            sx={{
              width: '100%',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
              border: `1px solid ${snackbar.severity === 'success' ? '#4CAF50' : '#C6A43F'}`,
              '& .MuiAlert-icon': {
                color: snackbar.severity === 'success' ? '#4CAF50' : '#C6A43F'
              }
            }}
          >
            {snackbar.message}
          </Alert>
        </motion.div>
      </Snackbar>
    </Box>
  );
};

export default Admin;